import { useState } from "react";
import { auth } from "../lib/apteva";
import { navigate, Link, useRoute } from "../lib/router";

export function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const route = useRoute();
  const hashParams = new URLSearchParams(route.split("?")[1] || "");
  const resetSuccess = hashParams.get("reset") === "success";
  const registered = hashParams.get("registered") === "true";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await auth.login(email, password, rememberMe);
      window.location.href = "/dashboard";
    } catch (err: any) {
      let msg = err.message || "Login failed";
      if (msg.toLowerCase().includes("algorithm") || msg.toLowerCase().includes("mismatch")) {
        msg = 'Your password needs to be reset. Please use "Forgot password?" below.';
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="inline-flex w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl items-center justify-center mb-4">
            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-stone-900 dark:text-white">Welcome back</h2>
          <p className="text-sm text-stone-500 dark:text-zinc-500 mt-1">Sign in to your SocialCast account</p>
        </div>

        {resetSuccess && (
          <div className="mb-4 p-3 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 rounded-xl text-sm border border-emerald-200/60 dark:border-emerald-500/20">
            Password reset successfully! You can now log in.
          </div>
        )}

        {registered && (
          <div className="mb-4 p-3 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 rounded-xl text-sm border border-emerald-200/60 dark:border-emerald-500/20">
            Account created! Please log in.
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 rounded-xl text-sm border border-red-200/60 dark:border-red-500/20">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 dark:text-zinc-300 mb-1.5">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full px-3.5 py-2.5 border border-stone-300 dark:border-zinc-700 rounded-xl bg-white dark:bg-zinc-800/50 text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 dark:focus:border-orange-500 transition-colors"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 dark:text-zinc-300 mb-1.5">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full px-3.5 py-2.5 border border-stone-300 dark:border-zinc-700 rounded-xl bg-white dark:bg-zinc-800/50 text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 dark:focus:border-orange-500 transition-colors"
              required
            />
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm text-stone-500 dark:text-zinc-400 cursor-pointer">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="rounded border-stone-300 dark:border-zinc-600 text-orange-500 focus:ring-orange-500"
              />
              Remember me
            </label>
            <Link href="/forgot-password" className="text-sm text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 font-medium">
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 px-4 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 disabled:opacity-50 transition-all"
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-stone-500 dark:text-zinc-500">
          Don't have an account?{" "}
          <Link href="/register" className="text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 font-semibold">
            Create Account
          </Link>
        </p>
      </div>
    </div>
  );
}
